import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import api from "../api";
import userIcon from "../assets/icons/users.png";

// For Excel export
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const BASE_URL = `${process.env.REACT_APP_API_URL}/activity-log/enterprise-users`; // 👈 updated route
const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(500);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [roleList, setRoleList] = useState([]);
  const [userRoleName, setUserRoleName] = useState("");
  const [totalEntries, setTotalEntries] = useState(0);

  const [logStats, setLogStats] = useState({
    totalLogs: 0,
    superAdminLogs: 0,
    adminLogs: 0,
    userLogs: 0,
  });

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  useEffect(() => {
    const fetchRolesAndUser = async () => {
      try {
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/roles/all`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const roles = res.data;

        //  console.log("Fetched roles:", roles);

        setRoleList(roles);

        setUserRoleName(roles.user_role || "User");
      } catch (err) {
        console.error("Error fetching roles or user info", err);
      }
    };

    fetchRolesAndUser();
  }, []);

  useEffect(() => {
    const fetchLogStats = async () => {
      try {
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/activity-log/stats`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setLogStats(res.data); // backend should return { totalLogs, adminLogs, ... }
      } catch (error) {
        console.error("Failed to fetch log stats", error);
      }
    };

    fetchLogStats();
  }, []);

  const logStatsList =
    userRoleName === "Superadmin"
      ? [
          { key: "totalLogs", label: "Total Logs" },
          { key: "superAdminLogs", label: "Super Admin Logs" },
          { key: "adminLogs", label: "Admin Logs" },
          // { key: "userLogs", label: "User Logs" },
        ]
      : userRoleName === "Admin"
      ? [
          { key: "totalLogs", label: "Total Logs" },
          { key: "adminLogs", label: "Admin Logs" },
          // { key: "userLogs", label: "User Logs" },
        ]
      : [];

  const handleReset = () => {
    setSortOrder("asc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleApply = () => {
    console.log("Sorting by:", sortOrder);
    console.log("Filtering by day:", dayFilter);
  };

  const fetchLogs = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}?page=${currentPage}&limit=${entriesPerPage}&search=${searchTerm}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) throw new Error("Failed to fetch logs");

      const data = await res.json();
      setLogs(data.data); // logs list
      setTotalEntries(data.total); // total count for pagination
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [searchTerm, currentPage, entriesPerPage]);

  const filteredLogs = logs
    .filter((log) => {
      // 🔍 Search filter
      const lower = searchTerm.toLowerCase();
      return (
        log.action.toLowerCase().includes(lower) ||
        (log.description && log.description.toLowerCase().includes(lower)) ||
        String(log.user_id).includes(lower) ||
        (log.ip_address && log.ip_address.toLowerCase().includes(lower)) ||
        (log.created_by && String(log.created_by).includes(lower)) ||
        (log.updated_by && String(log.updated_by).includes(lower))
      );
    })
    .filter((log) => {
      // 📅 Day filter
      const today = new Date();
      const logDate = new Date(log.updated_timestamp);

      if (dayFilter === "Today") {
        return logDate.toDateString() === today.toDateString();
      } else if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return logDate.toDateString() === yesterday.toDateString();
      }
      return true;
    })
    .sort((a, b) => {
      // ⏱ Sort by time
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  // logs is now directly the current page data from backend
  const currentData = logs;

  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const showingFrom =
    totalEntries === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const showingTo = Math.min(currentPage * entriesPerPage, totalEntries);

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1); // reset to page 1 on page size change
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this log?")) return;
    try {
      const res = await fetch(`${BASE_URL}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      await fetchLogs();
      if (currentData.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Export to Excel function
  const exportExcel = () => {
    // Prepare worksheet data (array of arrays)
    const wsData = [
      ["ID", "User ID", "Action", "IP Address", "Description","Updated Timestamp"],
      ...filteredLogs.map((log) => [
        log.id,
        log.user_id,
        log.action,
        log.ip_address || "",
        log.description || "",
        formatDateToUserTimeZone(log.updated_timestamp),
      ]),
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "ActivityLogs");

    // Write workbook and trigger download
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "activity_logs.xlsx");
  };

  return (
    <div className="mx-2 pt-1 h-100 overflow-hidden">
      <div className="d-flex flex-wrap gap-3 mb-2 mt-0 justify-content-evenly align-items-center">
        {logStatsList.map((item, index) => (
          <div key={index} className="dashboard-box p-3 rounded shadow-sm">
            <div className="d-flex align-items-center mb-2">
              <img
                src={userIcon}
                alt={item.label}
                style={{
                  width: "22px",
                  height: "22px",
                }}
                className="icon-image1"
              />
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>
            <h2 className="fw-bold">{logStats[item.key]}</h2>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold refresh-prompts" style={{ fontSize: "20px" }}>
          Activity Log Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[500, 1000, 1500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page:</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Logs"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn custom btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button>

          {/* Dropdown Filter Start */}

          <div className="">
            <button
              className="icon-button filter-dropdown-toggle action-button"
              type="button"
              id="filterDropdown"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              aria-labelledby="filterDropdown"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts px-0">Time</h6>
              <hr className="mt-0" />
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="sortOrder"
                  id="asc"
                  value="asc"
                  checked={sortOrder === "asc"}
                  onChange={() => setSortOrder("asc")}
                />
                <label
                  className="form-check-label notification-label"
                  htmlFor="asc"
                >
                  Ascending
                </label>
              </div>
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="sortOrder"
                  id="desc"
                  value="desc"
                  checked={sortOrder === "desc"}
                  onChange={() => setSortOrder("desc")}
                />
                <label
                  className="form-check-label notification-label"
                  htmlFor="desc"
                >
                  Descending
                </label>
              </div>

              <hr className="my-2" />
              <h6 className="fw-bolder refresh-prompts px-0">By Day</h6>
              <hr className="mt-0" />
              {["Today", "Yesterday"].map((day) => (
                <div className="form-check" key={day}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label
                    className="form-check-label fw-5 notification-label"
                    htmlFor={day}
                  >
                    {day}
                  </label>
                </div>
              ))}

              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="radio"
                  name="dayFilter"
                  id="all"
                  value=""
                  checked={dayFilter === ""}
                  onChange={() => setDayFilter("")}
                />
                <label
                  className="form-check-label fw-5 notification-label"
                  htmlFor="all"
                >
                  All
                </label>
              </div>

              <hr className="mt-1" />
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={handleApply}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="text-muted fs-5">No entries found</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table activity-table table-sm w-100">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>IP Address</th>
                  <th>Action</th>
                  <th>Description</th>
                  <th>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => (
                  <tr key={log.id}>
                    <td>{log.user_id || "—"}</td>
                    <td>{log.ip_address || "—"}</td>
                    <td title={log.action}>{log.action || "—"}</td>
                    <td title={log.description}>{log.description || "—"}</td>
                    <td>
                      {log.updated_timestamp
                        ? formatDateToUserTimeZone(log.updated_timestamp)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* <div className="d-flex justify-content-between align-items-center mt-2 table-footer"> */}
          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>

                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};
export default ActivityLog;
